/** tm/components/GovProgGroupBox.tsx — 공고 상세 「같은 사업 묶음」 상자 (2026-10-07g 지도사 「공고 고치기」 — 리포트 패널과 같은 액션을 로움에서도)
 *
 *  같은 공고(dup_group · 똑같은 공고가 여러 건)와 다르다 — 같은 사업(prog_group)은 운영기관 · 차수 · 유형(part)이 달라도 «한 사업»으로 보는 묶음.
 *  리포트(fphub) 로드맵은 묶음을 한 줄(가장 큰 금액 · 유형이 갈리면 유형 이름)로 접는다.
 *  값은 서버(GET /gov-programs/:id/fix · reportFix.editStateOf)에서 받고, 풀기 · 모두 풀기 · 묶기는 POST /gov-programs/:id/fix — 직접 정한 값은 AI 묶기가 다시 바꾸지 않는다.
 *  묶을 공고 고르기는 이미 받아 둔 등록 공고 목록(store.govPrograms)에서 이름 · 기관으로 거른다 — 새 API 없이, 3천 건이어도 즉시.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { GovFixAction, GovFixState, GovProgram } from '../types';
import * as api from '../api';
import { C, R, T, Badge, IMEText } from './ui';

interface Props {
  programId: string;
  /** 묶을 공고 후보 — 등록된 공고 전체(대표 · 식구 모두) */
  programs: GovProgram[];
  /** 식구 이름을 누르면 그 공고로 */
  onOpen: (id: string) => void;
  /** 반영 뒤 — 목록(store)을 새로 읽는다(카드의 「같은 사업 N건」 · 상세 값) */
  onChanged: () => Promise<void> | void;
  onToast: (msg: string) => void;
}

const box: React.CSSProperties = { marginTop: 10, padding: '8px 10px', borderRadius: R.md, border: `1px solid ${C.border}`, background: C.bgAlt, fontSize: T.xs, color: C.sub, lineHeight: 1.5 };
const btn = (color: string, bg: string, border: string, disabled = false): React.CSSProperties => ({
  height: 24, padding: '0 9px', borderRadius: R.sm, border: `1px solid ${border}`, background: bg, color, fontSize: T.xs, fontWeight: 700,
  cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', opacity: disabled ? 0.55 : 1, flexShrink: 0,
});
const row: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', borderRadius: R.sm, background: C.white, border: `1px solid ${C.border}`, minWidth: 0 };
const ell: React.CSSProperties = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const CAND_MAX = 8;

export default function GovProgGroupBox({ programId, programs, onOpen, onChanged, onToast }: Props) {
  const [state, setState] = useState<GovFixState | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState<string | null>(null);   // 「공고id:액션」 — 누른 단추만 잠근다
  const [pickOpen, setPickOpen] = useState(false);
  const [q, setQ] = useState('');
  const seq = useRef(0);   // 공고를 빨리 바꾸면 늦게 온 답을 버린다

  const load = useCallback(async () => {
    const my = ++seq.current;
    setLoading(true); setErr('');
    try {
      const r = await api.getGovFix(programId);
      if (my !== seq.current) return;
      if (!r.success || !r.data) throw new Error(r.error || '묶음 정보를 받지 못했습니다');
      setState(r.data);
    } catch (e) { if (my === seq.current) setErr(e instanceof Error ? e.message : '묶음 정보를 받지 못했습니다'); }
    finally { if (my === seq.current) setLoading(false); }
  }, [programId]);
  useEffect(() => { setState(null); setPickOpen(false); setQ(''); void load(); }, [load]);

  const members = useMemo(() => state?.group?.members ?? [], [state]);
  const memberIds = useMemo(() => new Set(members.map(m => m.id)), [members]);
  const grouped = members.length > 1;

  /** 액션 한 번 — 반영 → 이 상자 다시 받기 + 목록 새로 읽기 (둘 다 끝나야 단추가 풀린다) */
  async function run(targetId: string, body: GovFixAction, done: string) {
    if (busy) return;
    setBusy(`${targetId}:${body.action}`);
    try {
      const r = await api.postGovFix(targetId, body);
      if (!r.success) throw new Error(r.error || '처리에 실패했습니다');
      onToast(done);
      setPickOpen(false); setQ('');
      await Promise.all([load(), Promise.resolve(onChanged())]);
    } catch (e) { onToast(e instanceof Error ? e.message : '처리에 실패했습니다'); }
    finally { setBusy(null); }
  }

  // 묶을 공고 후보 — 자기 · 이미 식구인 것 빼고 이름 · 리포트 이름 · 짧은 이름 · 기관으로
  const cands = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    const hit = (v: string | null | undefined) => !!v && v.toLowerCase().includes(s);
    const out: GovProgram[] = [];
    for (const g of programs) {
      if (g.id === programId || memberIds.has(g.id)) continue;
      if (hit(g.name) || hit(g.report_name) || hit(g.brief_name) || hit(g.agency)) { out.push(g); if (out.length >= CAND_MAX) break; }
    }
    return out;
  }, [q, programs, programId, memberIds]);

  const picker = (
    <div style={{ marginTop: 6 }}>
      {!pickOpen ? (
        <button type="button" disabled={!!busy || loading} onClick={() => setPickOpen(true)} title="다른 공고를 골라 같은 사업으로 묶습니다 — 리포트에는 한 줄로 나갑니다"
          style={btn(C.accent, C.white, `${C.accent}55`, !!busy || loading)}>＋ 다른 공고와 묶기</button>
      ) : (
        <div style={{ padding: '6px 8px', borderRadius: R.sm, background: C.white, border: `1px solid ${C.accent}55` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <IMEText value={q} onChange={setQ} autoFocus placeholder="사업명 · 리포트 이름 · 기관으로 찾기" maxLength={100}
              onKeyDown={e => { if (e.key === 'Escape') { setPickOpen(false); setQ(''); } }}
              style={{ flex: 1, minWidth: 0, padding: '6px 9px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm, outline: 'none', boxSizing: 'border-box' }} />
            <button type="button" onClick={() => { setPickOpen(false); setQ(''); }} style={btn(C.sub, C.white, C.border)}>닫기</button>
          </div>
          {q.trim() && (
            <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {cands.length === 0 && <div style={{ fontSize: T.xs, color: C.muted, padding: '2px 0' }}>맞는 공고가 없습니다 (자기 공고 · 이미 묶인 식구는 빠집니다)</div>}
              {cands.map(g => (
                <div key={g.id} style={row}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ ...ell, fontSize: T.xs, fontWeight: 600, color: C.text }} title={g.name}>{g.name}{g.is_active === false ? ' (비활성)' : ''}</div>
                    <div style={{ ...ell, fontSize: T.xs, color: C.muted }}>{[g.agency || null, g.brief_name ? `짧은 이름 ${g.brief_name}` : null, g.prog_group ? '다른 묶음에 있음 — 묶으면 그 식구도 함께' : null].filter(Boolean).join(' · ')}</div>
                  </div>
                  <button type="button" disabled={!!busy} onClick={() => void run(programId, { action: 'group_merge', programIds: [g.id] }, '같은 사업으로 묶었습니다 — 리포트에는 한 줄로 나갑니다')}
                    style={btn(C.white, C.accent, C.accent, !!busy)}>묶기</button>
                </div>
              ))}
              {cands.length >= CAND_MAX && <div style={{ fontSize: T.xs, color: C.muted }}>{CAND_MAX}건만 보입니다 — 더 좁혀 주세요</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (err && !state) {
    return (
      <div style={box}>
        <span style={{ fontWeight: 700, color: C.text }}>같은 사업 묶음</span> — {err}
        <button type="button" onClick={() => void load()} style={{ ...btn(C.sub, C.white, C.border), marginLeft: 8 }}>다시 받기</button>
      </div>
    );
  }
  if (!state) return <div style={box}><span style={{ fontWeight: 700, color: C.text }}>같은 사업 묶음</span> — {loading ? '받는 중…' : ''}</div>;

  if (!grouped) {
    return (
      <div style={box}>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
          <span style={{ fontWeight: 700, color: C.text }}>같은 사업 묶음 없음</span>
          <span style={{ color: C.muted }}>— 운영기관 · 차수 · 유형만 다른 공고가 또 있으면 묶어 두세요. 리포트에는 한 줄로 나갑니다</span>
          {state.group.by === 'manual' && <Badge bg={C.white} color={C.sub} style={{ border: `1px solid ${C.border}` }}>직접 풀어 둠 — 자동으로 다시 묶지 않음</Badge>}
        </div>
        {picker}
      </div>
    );
  }

  return (
    <div style={box}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        <span style={{ fontWeight: 700, color: C.text, whiteSpace: 'nowrap' }}>같은 사업 {members.length}건 묶음</span>
        {state.group.by === 'manual' && <Badge bg={C.white} color={C.sub} style={{ border: `1px solid ${C.border}` }}>직접 정함</Badge>}
        <button type="button" disabled={!!busy || loading} onClick={() => { if (window.confirm(`${members.length}건을 모두 따로 둡니다. 리포트에는 각각 한 줄씩 나갑니다.`)) void run(programId, { action: 'group_split', all: true }, '묶음을 모두 풀었습니다'); }}
          title="묶음 전체를 풉니다 — 각 공고가 따로 보이고 자동으로 다시 묶지 않습니다" style={{ ...btn(C.sub, C.white, C.border, !!busy || loading), marginLeft: 'auto' }}>모두 풀기</button>
      </div>
      <div style={{ color: C.muted, marginTop: 2 }}>리포트 로드맵에는 한 줄로 나갑니다 — 유형이 갈리면 유형 이름 · 금액은 가장 큰 것</div>
      <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {members.map(m => {
          const me = m.id === programId;
          const k = `${m.id}:group_split`;
          return (
            <div key={m.id} style={{ ...row, ...(me ? { borderColor: `${C.accent}66`, background: C.accentLt } : {}) }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                  {me ? (
                    <span style={{ ...ell, fontSize: T.xs, fontWeight: 600, color: C.text }} title={m.name}>{m.name} <span style={{ color: C.accent, fontWeight: 700 }}>(이 공고)</span></span>
                  ) : (
                    <span role="button" tabIndex={0} onClick={() => onOpen(m.id)} onKeyDown={e => { if (e.key === 'Enter') onOpen(m.id); }} title="이 공고 보기"
                      style={{ ...ell, fontSize: T.xs, fontWeight: 600, color: C.text, cursor: 'pointer' }}>{m.name}</span>
                  )}
                  {m.part && <Badge bg="#FAEEDA" color="#633806" style={{ padding: '1px 6px' }}>{m.part}</Badge>}
                  {m.by === 'manual' && <Badge bg={C.white} color={C.sub} style={{ padding: '1px 6px', border: `1px solid ${C.border}` }}>직접</Badge>}
                </div>
                <div style={{ ...ell, fontSize: T.xs, color: C.muted }}>{[m.agency || null, m.brief_name ? `리포트 「${m.brief_name}」` : null].filter(Boolean).join(' · ') || '—'}</div>
              </div>
              <button type="button" disabled={!!busy || loading} onClick={() => void run(m.id, { action: 'group_split' }, me ? '이 공고를 묶음에서 뺐습니다' : `「${m.name}」을 묶음에서 뺐습니다`)}
                title={me ? '이 공고만 묶음에서 뺍니다 — 나머지는 그대로 묶여 있습니다' : '이 공고만 묶음에서 뺍니다'} style={btn(C.sub, C.white, C.border, !!busy || loading)}>
                {busy === k ? '푸는 중…' : me ? '이 공고 풀기' : '풀기'}
              </button>
            </div>
          );
        })}
      </div>
      {picker}
    </div>
  );
}
